import type { AvailableOnSide, Finish, FinishConstraints } from "@tosize/shapes/src/types/finish";
import type { FinishItemPostData } from "@tosize/shapes/src/types/workpiece";
import { produce } from "immer";
import { useCallback } from "react";
import { usePostDataContext } from "../context/PostDataContext";

export function getDefaultOptionForFinish(finish: Finish): string {
	return Object.keys(finish.options)[0] ?? "";
}

function getDefaultOptionsForFinish(
	finish: Finish,
	option?: string,
	parameters?: FinishItemPostData["option_parameters"],
) {
	const defaultOption = option ?? getDefaultOptionForFinish(finish);
	const defaultOptions: FinishItemPostData["option_parameters"] = {};
	const fields: FinishConstraints["fields"] =
		finish.options[String(defaultOption)]?.constraints.fields;

	// set previous value if available
	// if previous value is undefined set lowest value
	// if not available set closest value
	if (fields) {
		for (const field of Object.values(fields)) {
			if (!field.values) continue;

			if (parameters === undefined) {
				defaultOptions[field.name] = Number(field.values[0]?.value);
			} else if (
				!!parameters &&
				field.values.some(({ value }) => Number(value) === Object.values(parameters)[0])
			) {
				defaultOptions[field.name] = Number(Object.values(parameters)[0]);
			} else if (field.values.length) {
				defaultOptions[field.name] = Number(field.values[field.values.length - 1]?.value);
			}
		}
	}

	return defaultOptions;
}

const useSelectedFinishes = () => {
	const { workpiece, setWorkpiece } = usePostDataContext();

	const getSelectedFinishesOnSide = useCallback(
		(side: AvailableOnSide) => {
			const selectedSide = workpiece.finishes.side[side.sideCode];

			return selectedSide ? Object.keys(selectedSide) : [];
		},
		[workpiece.finishes],
	);

	const applyFinishOnSides = (finish: Finish, sides: AvailableOnSide[]) => {
		setWorkpiece(
			produce((state) => {
				for (const side of sides) {
					const current = state.finishes.side;

					if (current) {
						current[side.sideCode] = {
							[side.finishCode]: [
								{
									option: getDefaultOptionForFinish(finish),
									option_parameters: getDefaultOptionsForFinish(finish),
								},
							],
						};
					} else {
						console.error("current Finish is undefined");
					}
				}
			}),
		);
	};

	const applyFinishOptionOnSides = (
		finish: Finish,
		sides: AvailableOnSide[],
		index: number,
		option: FinishItemPostData["option"],
		parameters: FinishItemPostData["option_parameters"],
	) =>
		setWorkpiece(
			produce((state) => {
				for (const side of sides) {
					const current = state.finishes.side[side.sideCode]?.[side.finishCode];

					if (current) {
						current[index] = {
							option,
							option_parameters: getDefaultOptionsForFinish(finish, option, parameters),
						};
					} else {
						console.error("current Finish Option is undefined");
					}
				}
			}),
		);

	const applyFinishOptionParameterOnSides = (
		sides: AvailableOnSide[],
		index: number,
		optionCode: string,
		value: string,
	) =>
		setWorkpiece(
			produce((state) => {
				for (const side of sides) {
					const current = state.finishes.side[side.sideCode]?.[side.finishCode]?.[index];

					if (current) {
						current.option_parameters = {
							...current.option_parameters,
							[optionCode]: Number.isNaN(value) ? value : Number(value),
						};
					} else {
						console.error("current Finish Option Parameter is undefined");
					}
				}
			}),
		);

	const removeFinishSides = (sides: AvailableOnSide[]) => {
		setWorkpiece(
			produce((state) => {
				for (const item of sides) {
					delete state.finishes.side[item.sideCode];
				}
			}),
		);
	};

	const reset = () => {
		setWorkpiece(
			produce((state) => {
				state.finishes.side = {};
			}),
		);
	};

	return {
		getSelectedFinishesOnSide,
		applyFinishOnSides,
		applyFinishOptionOnSides,
		applyFinishOptionParameterOnSides,
		removeFinishSides,
		reset,
	} as const;
};

export default useSelectedFinishes;
